// Tile distributions
// Named 72-tile variants live in @wordsheet/shared (single source of truth for
// client, worker, and agent). The size tables below are legacy fixed-size bags,
// selectable via the admin panel.
import {
    TILE_BAG_VARIANTS,
    DEFAULT_VARIANT_KEY,
    isVariantKey,
    getVariant,
    pickRandomVariantKey
} from '@wordsheet/shared/tile-bags';

// 60 tiles (ratio 0.36 tiles/square)
const TILE_DISTRIBUTION_60 = {
    'E': 7, 'T': 4, 'A': 5, 'O': 5, 'I': 5, 'N': 3, 'S': 3, 'R': 3,
    'H': 2, 'L': 2, 'D': 2,
    'C': 1, 'U': 3, 'M': 1, 'W': 1, 'F': 1, 'G': 1, 'Y': 1, 'P': 1,
    'B': 1, 'V': 1, 'K': 1,
    'J': 1, 'X': 1,
    'Q': 1, 'Z': 1,
    ' ': 2
};

// 65 tiles (ratio 0.38 tiles/square)
const TILE_DISTRIBUTION_65 = {
    'E': 8, 'T': 4, 'A': 6, 'O': 5, 'I': 5, 'N': 4, 'S': 4, 'R': 4,
    'H': 2, 'L': 2, 'D': 2,
    'C': 1, 'U': 3, 'M': 1, 'W': 1, 'F': 1, 'G': 1, 'Y': 1, 'P': 1,
    'B': 1, 'V': 1, 'K': 1,
    'J': 1, 'X': 1,
    'Q': 1, 'Z': 1,
    ' ': 2
};

// 80 tiles (ratio 0.47 tiles/square) - extended games
const TILE_DISTRIBUTION_80 = {
    'E': 10, 'T': 5, 'A': 7, 'O': 6, 'I': 7, 'N': 5, 'S': 4, 'R': 5,
    'H': 2, 'L': 3, 'D': 3,
    'C': 2, 'U': 3, 'M': 2, 'W': 1, 'F': 1, 'G': 2, 'Y': 2, 'P': 1,
    'B': 1, 'V': 1, 'K': 1,
    'J': 1, 'X': 1,
    'Q': 1, 'Z': 1,
    ' ': 2
};

export const TILE_DISTRIBUTIONS = {
    '60': { name: 'Compact 60', distribution: TILE_DISTRIBUTION_60 },
    '65': { name: 'Light 65', distribution: TILE_DISTRIBUTION_65 },
    '80': { name: 'Extended 80', distribution: TILE_DISTRIBUTION_80 },
};

// Resolve the tileBag flag value to a concrete bag choice.
// - a variant key (e.g. 'spiceRack') → that named variant
// - a legacy size key ('60'/'65'/'80') → that fixed-size bag
// - anything else (including the default '72') → a random named variant
export function resolveBagSelection(flagValue) {
    if (isVariantKey(flagValue)) {
        return { key: flagValue, name: TILE_BAG_VARIANTS[flagValue].name };
    }
    if (flagValue && TILE_DISTRIBUTIONS[flagValue]) {
        return { key: flagValue, name: TILE_DISTRIBUTIONS[flagValue].name };
    }
    const key = pickRandomVariantKey();
    return { key, name: TILE_BAG_VARIANTS[key].name };
}

// Look up the distribution for a bag key (variant or size), with default fallback
function getDistributionForKey(key) {
    if (isVariantKey(key)) return TILE_BAG_VARIANTS[key].distribution;
    return TILE_DISTRIBUTIONS[key]?.distribution || getVariant(DEFAULT_VARIANT_KEY).distribution;
}

// Point values per letter (shared across all distributions)
const TILE_POINTS = {
    'E': 1, 'T': 1, 'A': 1, 'O': 1, 'I': 1, 'N': 1, 'S': 1, 'R': 1,
    'H': 2, 'L': 2, 'D': 2,
    'C': 3, 'U': 3, 'M': 3, 'W': 3, 'F': 3, 'G': 3, 'Y': 3, 'P': 3,
    'B': 5, 'V': 5, 'K': 5,
    'J': 8, 'X': 8,
    'Q': 12, 'Z': 12,
    ' ': 0
};

// Legacy export for compatibility
export const TILE_DATA = Object.fromEntries(
    Object.entries(TILE_DISTRIBUTION_60).map(([letter, count]) => [
        letter,
        { points: TILE_POINTS[letter], count }
    ])
);

export class TileBag {
    constructor(distributionKey = null) {
        this.tiles = [];
        this.distributionKey = distributionKey;
        this.reset();
    }

    reset() {
        this.tiles = [];

        // Key may be a named variant ('houseBlend', ...) or a legacy size ('60'/'65'/'80');
        // unknown/missing keys fall back to the default variant
        const distKey = this.distributionKey || DEFAULT_VARIANT_KEY;

        const distribution = getDistributionForKey(distKey);

        for (const [letter, count] of Object.entries(distribution)) {
            const points = TILE_POINTS[letter];
            for (let i = 0; i < count; i++) {
                this.tiles.push({
                    letter: letter,
                    points: points,
                    isBlank: letter === ' ',
                    id: `${letter}-${i}-${Date.now()}-${Math.random()}`
                });
            }
        }
        this.shuffle();
    }

    shuffle() {
        for (let i = this.tiles.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.tiles[i], this.tiles[j]] = [this.tiles[j], this.tiles[i]];
        }
    }

    draw(count) {
        const drawn = [];
        for (let i = 0; i < count && this.tiles.length > 0; i++) {
            drawn.push(this.tiles.pop());
        }
        return drawn;
    }

    remaining() {
        return this.tiles.length;
    }

    returnTiles(tiles) {
        this.tiles.push(...tiles);
        this.shuffle();
    }

    // Serialize tile bag for Firestore storage
    toArray() {
        return this.tiles.map(tile => ({
            letter: tile.letter,
            points: tile.points,
            isBlank: tile.isBlank,
            id: tile.id
        }));
    }

    // Load tile bag from Firestore data
    static fromArray(tileArray) {
        const bag = new TileBag();
        bag.tiles = tileArray.map(t => ({
            letter: t.letter,
            points: t.points,
            isBlank: t.isBlank,
            id: t.id,
            assignedLetter: t.assignedLetter || null
        }));
        return bag;
    }
}

// Generate supplemental tiles using the game's bag distribution as weights
export function generateSupplementalTiles(count, bagKey = null) {
    const pool = [];
    for (const [letter, letterCount] of Object.entries(getDistributionForKey(bagKey))) {
        for (let i = 0; i < letterCount; i++) {
            pool.push({ letter, points: TILE_POINTS[letter], isBlank: letter === ' ' });
        }
    }

    const tiles = [];
    for (let i = 0; i < count; i++) {
        const pick = pool[Math.floor(Math.random() * pool.length)];
        tiles.push({
            letter: pick.letter,
            points: pick.points,
            isBlank: pick.isBlank,
            id: `${pick.letter}-sup-${Date.now()}-${Math.random()}`
        });
    }

    return tiles;
}

export function getPointValue(letter) {
    return TILE_DATA[letter.toUpperCase()]?.points ?? 0;
}

export function createTileElement(tile, draggable = true) {
    const div = document.createElement('div');
    div.className = 'tile';
    div.dataset.id = tile.id;
    div.dataset.letter = tile.letter;
    div.dataset.points = tile.points;
    div.draggable = draggable;

    if (tile.isBlank) {
        div.classList.add('blank');
        div.textContent = tile.assignedLetter || '?';
    } else {
        div.textContent = tile.letter;
    }

    if (tile.points > 0) {
        const pointsSpan = document.createElement('span');
        pointsSpan.className = 'points';
        pointsSpan.textContent = tile.points;
        div.appendChild(pointsSpan);
    }

    return div;
}
